import React, { useContext, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { DropDown, ConfirmationModal, Flex } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { HELP_DESK_PRIORITY_LABEL_KEYS, HELP_DESK_PRIORITIES } from '@ubeya/shared/constants/helpDesk';
import { PRIORITY_ICON_MAP, VISIBLE_PRIORITIES, HELP_DESK_ICON_MAP } from '../helpDeskConstants';
import { AssigneeDisplay, PriorityDisplay, CategoryDisplay } from './shared';

// region: Styled components

const getAdminSearchLabel = (admin) => {
  if (!admin) return '';
  const parts = [admin.name, admin.firstName, admin.lastName, admin.email].filter(Boolean);
  return parts.join(' ').trim() || String(admin.id);
};

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-block: 4px;
  padding-inline: ${({ $roundContent }) => ($roundContent ? 4 : 8)}px 8px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  border: 1px solid transparent;
  background: ${({ theme }) => theme.colors.grey1};

  ${({ $disabled, $isOpen, theme }) =>
    $disabled
      ? css`
          color: ${theme.colors.grey4};
          cursor: default;
        `
      : css`
          color: ${theme.colors.black};
          cursor: pointer;

          &:hover {
            background: ${theme.colors.lightHoverBackground};
          }

          ${$isOpen &&
          css`
            background: ${theme.colors.lightHoverBackground};
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
          `}
        `}
`;

const NoAssigneeWrap = styled(Flex)`
  gap: 8px;
`;

const CategoryOption = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const assigneeDropdownPortalStyle = {
  width: 260,
  '> :first-child': {
    margin: 0,
    paddingTop: 8,
    zIndex: 2
  },
  '.drop-down-search input': {
    borderRadius: 4
  }
};

const HelpDeskTicketMetadata = ({
  ticket,
  categories,
  admins,
  isEditable,
  isLoading,
  onCategoryChange,
  onPriorityChange,
  onAssigneeChange,
  compact = false,
  truncateAssignee = false,
  children
}) => {
  const { t } = useContext(ConfigContext);
  const theme = useTheme();

  // Current category info
  const currentCategory = useMemo(() => categories.find((c) => c.id === ticket?.categoryId), [
    categories,
    ticket?.categoryId
  ]);
  const categoryForDisplay = currentCategory
    ? { name: currentCategory.name, icon: currentCategory.icon, color: currentCategory.color || theme.colors.primary }
    : {
        name: ticket?.categoryName || ticket?.category?.name || t('help_desk_no_category'),
        icon: ticket?.category?.icon,
        color: ticket?.category?.color || theme.colors.primary
      };

  // Current assignee info — prefer the admin from the admins list (has image),
  // but fall back to denormalized ticket data so the avatar stays consistent
  // with the ticket list even when the admin is not in the current admins list.
  const currentAssignee = useMemo(() => admins.find((a) => a.id === ticket?.assignedAdminId), [
    admins,
    ticket?.assignedAdminId
  ]);
  const assigneeForDisplay =
    currentAssignee ||
    (ticket?.assignedAdminId && ticket?.assigneeName
      ? { name: ticket.assigneeName, image: ticket.assigneeAvatarUrl || null }
      : null);
  const assigneeName =
    currentAssignee?.name || currentAssignee?.firstName || ticket?.assigneeName || t('help_desk_no_assignee');

  // Current priority
  const currentPriority = ticket?.priority || HELP_DESK_PRIORITIES.NONE;

  return (
    <>
      {/* Category dropdown */}
      <DropDown
        showArrow={false}
        usePortal
        menuMinWidth={180}
        isDisabled={!isEditable || isLoading}
        renderValue={({ isOpen }) => (
          <MetaItem $disabled={!isEditable} $isOpen={isOpen}>
            <CategoryDisplay category={categoryForDisplay} compact={compact} />
          </MetaItem>
        )}>
        {categories.map((cat) => {
          const CatIcon = cat.icon && HELP_DESK_ICON_MAP[cat.icon];
          return (
            <DropDown.Item
              key={cat.id}
              isSelected={cat.id === ticket?.categoryId}
              onClick={() => onCategoryChange(cat.id)}>
              <CategoryOption>
                {CatIcon && (
                  <span
                    style={{
                      color: cat.color,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      flexShrink: 0
                    }}>
                    <CatIcon />
                  </span>
                )}
                {cat.name}
              </CategoryOption>
            </DropDown.Item>
          );
        })}
      </DropDown>

      {/* Assignee dropdown */}
      <DropDown
        showArrow={false}
        usePortal
        menuMinWidth={260}
        maxHeight={260}
        portalStyle={assigneeDropdownPortalStyle}
        showSearch
        isDisabled={!isEditable || isLoading}
        renderValue={({ isOpen }) => (
          <MetaItem $disabled={!isEditable} $isOpen={isOpen} $roundContent>
            <AssigneeDisplay
              admin={assigneeForDisplay}
              compact={compact}
              disabled={!isEditable}
              truncate={truncateAssignee}
            />
            {!compact && !assigneeForDisplay && assigneeName}
          </MetaItem>
        )}>
        <DropDown.Item
          key="unassigned"
          rawSearchLabel={t('help_desk_no_assignee')}
          isSelected={!ticket?.assignedAdminId}
          onClick={() => onAssigneeChange(null)}>
          <NoAssigneeWrap>
            <AssigneeDisplay admin={null} compact={false} disabled={!isEditable} />
            {t('help_desk_no_assignee')}
          </NoAssigneeWrap>
        </DropDown.Item>

        {admins.map((adm) => (
          <DropDown.Item
            key={adm.id}
            rawSearchLabel={getAdminSearchLabel(adm)}
            isSelected={adm.id === ticket?.assignedAdminId}
            onClick={() => onAssigneeChange(adm.id)}>
            <AssigneeDisplay admin={adm} />
          </DropDown.Item>
        ))}
      </DropDown>

      {/* Priority dropdown */}
      <DropDown
        showArrow={false}
        usePortal
        menuMinWidth={150}
        isDisabled={!isEditable || isLoading}
        renderValue={({ isOpen }) => (
          <MetaItem $disabled={!isEditable} $isOpen={isOpen}>
            <PriorityDisplay priority={currentPriority} compact={compact} disabled={!isEditable} />
          </MetaItem>
        )}>
        {VISIBLE_PRIORITIES.map((p) => (
          <DropDown.Item
            key={p}
            icon={PRIORITY_ICON_MAP[p]}
            isSelected={p === currentPriority}
            onClick={() => onPriorityChange(p)}>
            {t(HELP_DESK_PRIORITY_LABEL_KEYS[p])}
          </DropDown.Item>
        ))}
      </DropDown>

      {children}
    </>
  );
};

const HelpDeskCategoryConfirmModal = ({ pendingCategory, onConfirm, onClose }) => {
  if (pendingCategory === null) return null;
  return (
    <ConfirmationModal
      title="help_desk_change_category"
      body="help_desk_change_category_confirm"
      confirmText="confirm"
      cancelText="cancel"
      onConfirm={onConfirm}
      onClose={onClose}
      usePortal
    />
  );
};

export default HelpDeskTicketMetadata;
export { HelpDeskCategoryConfirmModal };
